import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle } from 'lucide-react'
import { objectsApi } from '../api'
import { RunbookTable } from './RunbookTable'
import { FeatheredScrollArea } from '@/components/ui/feathered-scroll-area'
import { calculateRunbookTimes } from '../utils/calculateRunbookTimes'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import type { RunbookTask } from '@/types/entities'

interface CloneRunbookDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string
    scenario: string
    onApply: (sourceObjectID: string, sourceScenario: string, customizedTasks?: RunbookTask[]) => Promise<void>
}

export function CloneRunbookDialog({
    open,
    onOpenChange,
    objectID,
    scenario,
    onApply,
}: CloneRunbookDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const [selectedObjectID, setSelectedObjectID] = useState<string | null>(null)
    const [selectedScenario, setSelectedScenario] = useState<string | null>(null)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [tasks, setTasks] = useState<RunbookTask[]>([])
    const [isApplying, setIsApplying] = useState(false)
    const [isLoadingRunbook, setIsLoadingRunbook] = useState(false)
    // Store the customized tasks from RunbookTable
    const customizedTasksRef = useRef<RunbookTask[]>([])

    // Fetch all objects for dropdown
    const { data: objectsData } = useQuery({
        queryKey: ['objects', 'all'],
        queryFn: async () => {
            const response = await objectsApi.list({ page: 1, limit: 10000 })
            return response.data?.data || response.data?.items || []
        },
    })

    // Fetch scenarios for selected object
    const { data: scenariosData } = useQuery({
        queryKey: ['objects', selectedObjectID, 'scenarios'],
        queryFn: async () => {
            if (!selectedObjectID) return []
            const decodedId = decodeURIComponent(selectedObjectID)
            const response = await objectsApi.getScenarios(decodedId)
            return response.data?.data || response.data || []
        },
        enabled: !!selectedObjectID,
    })

    // Fetch runbook when object and scenario are selected
    useEffect(() => {
        if (selectedObjectID && selectedScenario) {
            setIsLoadingRunbook(true)
            const decodedId = decodeURIComponent(selectedObjectID)
            const scenarioId = parseInt(selectedScenario)
            
            objectsApi.getRunbook(decodedId, selectedScenario)
                .then((response) => {
                    const fetchedTasks = response.data?.data || response.data || []
                    if (fetchedTasks && fetchedTasks.length > 0) {
                        // Convert to RunbookTask format and calculate times
                        const convertedTasks: RunbookTask[] = fetchedTasks.map((task: any) => ({
                            id: null, // New tasks, no ID yet
                            taskNo: task.taskNo || null,
                            task: task.task || '',
                            commands: task.commands || '',
                            duration: task.duration || 0,
                            startTime: null,
                            finishTime: null,
                            dependencies: task.dependencies || (task.taskNo === 1 ? '' : '1'),
                        }))
                        
                        const tasksWithTimes = calculateRunbookTimes(convertedTasks)
                        setTasks(tasksWithTimes)
                    } else {
                        setTasks([])
                    }
                })
                .catch((error) => {
                    console.error('Error fetching runbook:', error)
                    setTasks([])
                    toast({
                        title: t('common.error'),
                        description: handleApiError(error),
                        variant: 'destructive',
                    })
                })
                .finally(() => {
                    setIsLoadingRunbook(false)
                })
        } else {
            setTasks([])
        }
    }, [selectedObjectID, selectedScenario, t, toast])

    const handleApply = async () => {
        if (!selectedObjectID || !selectedScenario) return
        
        setShowConfirmDialog(true)
    }

    const handleConfirmApply = async () => {
        if (!selectedObjectID || !selectedScenario) return
        
        setIsApplying(true)
        try {
            // Get the current tasks - use customized tasks if available, otherwise use the current tasks state
            let tasksToSave: RunbookTask[] | undefined
            
            if (customizedTasksRef.current.length > 0) {
                tasksToSave = customizedTasksRef.current
            } else if (tasks.length > 0) {
                tasksToSave = calculateRunbookTimes(tasks)
            }
            
            await onApply(selectedObjectID, selectedScenario, tasksToSave)
            
            toast({
                title: t('common.success'),
                description: t('objects.runbookClonedSuccessfully'),
            })
            
            setShowConfirmDialog(false)
            onOpenChange(false)
            setSelectedObjectID(null)
            setSelectedScenario(null)
            setTasks([])
            customizedTasksRef.current = []
        } catch (error) {
            console.error('Error cloning runbook:', error)
            toast({
                title: t('common.error'),
                description: handleApiError(error),
                variant: 'destructive',
            })
        } finally {
            setIsApplying(false)
        }
    }

    const handleCancel = () => {
        setShowConfirmDialog(false)
        setSelectedObjectID(null)
        setSelectedScenario(null)
        setTasks([])
        customizedTasksRef.current = []
    }
    
    // Reset customized tasks when dialog closes
    useEffect(() => {
        if (!open) {
            customizedTasksRef.current = []
            setSelectedObjectID(null)
            setSelectedScenario(null)
            setTasks([])
        }
    }, [open])

    return (
        <>
            <Dialog open={open && !showConfirmDialog} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{t('objects.cloneRunbook')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.cloneRunbookDescription')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>{t('objects.sourceObjectID')}</Label>
                                <Select
                                    value={selectedObjectID || ''}
                                    onValueChange={(value) => {
                                        setSelectedObjectID(value || null)
                                        setSelectedScenario(null) // Reset scenario when object changes
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('objects.selectObjectID')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {objectsData?.filter((obj: any) => obj.objectID !== objectID).map((obj: any) => (
                                            <SelectItem key={obj.objectID} value={obj.objectID}>
                                                {obj.objectID} - {obj.objectDescription || ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>{t('objects.sourceScenario')}</Label>
                                <Select
                                    value={selectedScenario || ''}
                                    onValueChange={(value) => {
                                        setSelectedScenario(value || null)
                                    }}
                                    disabled={!selectedObjectID}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('objects.selectScenario')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {scenariosData?.map((scenario: any) => (
                                            <SelectItem key={scenario.id} value={scenario.id.toString()}>
                                                {scenario.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {isLoadingRunbook && (
                            <div className="flex items-center justify-center h-32">
                                <div className="text-muted-foreground">{t('objects.service.loading')}</div>
                            </div>
                        )}

                        {!isLoadingRunbook && tasks.length > 0 && (
                            <div className="space-y-2">
                                <Label>{t('objects.runbookTasks')}</Label>
                                <div className="border rounded-md">
                                    <FeatheredScrollArea viewportClassName="max-h-[55vh]">
                                        <RunbookTable
                                            tasks={tasks}
                                            onSave={(updatedTasks) => {
                                                customizedTasksRef.current = updatedTasks
                                            }}
                                            autoUpdate={true}
                                            onAutoUpdateChange={() => {}}
                                            isSaving={false}
                                            hideToolbar={true}
                                        />
                                    </FeatheredScrollArea>
                                </div>
                            </div>
                        )}

                        {!isLoadingRunbook && selectedObjectID && selectedScenario && tasks.length === 0 && (
                            <Alert>
                                <AlertDescription>
                                    {t('objects.noRunbookFound')}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="flex justify-end space-x-2 pt-4 border-t">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    onOpenChange(false)
                                    handleCancel()
                                }}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="button"
                                onClick={handleApply}
                                disabled={!selectedObjectID || !selectedScenario || tasks.length === 0}
                            >
                                {t('objects.apply')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('objects.confirmCloneRunbook')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.confirmCloneRunbookDescription')}
                        </DialogDescription>
                    </DialogHeader>

                    <Alert variant="destructive" className="mt-4">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('objects.cloneRunbookWarning')}
                        </AlertDescription>
                    </Alert>

                    <div className="flex justify-end space-x-2 pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowConfirmDialog(false)}
                            disabled={isApplying}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={handleConfirmApply}
                            disabled={isApplying}
                        >
                            {isApplying ? t('objects.applying') : t('common.yes')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
